// app/src/lib/delivery/normalizer.js — OCR extract → canonical statement record.
//
// Real platform statements are itemized: a gross-sales line, a net-payout line,
// and many leaf charge/credit/tax/tip lines that vary wildly in wording per
// platform. So the OCR prompt transcribes EVERY leaf line (label + signed
// amount), and this normalizer is the deterministic categorizer: each line is
// bucketed exactly once (commission/marketing/fees/tax/tips/other), which is
// what keeps tax from being double-counted (Uber lists it inside "amendments")
// and lets DoorDash — which never shows tax — reconcile with tax = 0.
//
//   netPayout  ==  grossSales + Σ(all signed line amounts)
//
// Deductions arrive as NEGATIVE amounts (money the platform kept); we store
// their positive magnitude in the deduction buckets. Pass-throughs (tax, tips)
// arrive POSITIVE and are stored as-is. So takeCents = Σ deduction buckets and
// the reconciliation residual is ~0 when every leaf line was captured.

import {
  BUCKET_KEYS, normalizePlatform, reconcileResidualCents, categorizeLine,
} from './model.js';

// Slack (cents) for rounding / a missed minor line before we flag for review.
const RECONCILE_TOLERANCE_CENTS = 500; // $5 — these are large monthly statements
const MAX_LINES = 80;

// Sales-channel lines that are GROSS components, not deductions (Grubhub
// "Marketplace orders", "Partner orders", "Storefront orders"). Specific to
// "... orders" so it never matches a fee line like Uber's "Marketplace Fees".
const SALES_CHANNEL_RE = /(marketplace|partner|storefront|pickup|self-?delivery)\s+orders?\b/i;

/** dollars (number|string like "-$1,234.56") → integer cents (signed). Missing → 0. */
export function dollarsToCents(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

// First present, finite value among several candidate keys (label drift).
function pick(o, ...keys) {
  for (const k of keys) if (o[k] != null && o[k] !== '') return o[k];
  return undefined;
}

/** YYYY-MM from a YYYY-MM-DD (or YYYY-MM) string; null if unparseable. */
export function monthOf(dateStr) {
  const m = /^(\d{4})-(\d{2})/.exec(String(dateStr || ''));
  return m ? `${m[1]}-${m[2]}` : null;
}

const zeroBuckets = () => Object.fromEntries(BUCKET_KEYS.map((k) => [k, 0]));

/**
 * Categorize raw leaf line items into canonical buckets.
 * @param {Array<{label?:string, amount?:number|string}>} items
 * @returns {{ buckets:Object, lines:Array<{label:string,amountCents:number,category:string}> }}
 */
function foldLines(items) {
  const buckets = zeroBuckets();
  const lines = [];
  for (const it of (items || []).slice(0, MAX_LINES)) {
    const amountCents = dollarsToCents(it.amount); // signed exactly as printed
    const category = categorizeLine(it.label);
    lines.push({ label: String(it.label || '').slice(0, 80), amountCents, category });
    buckets[`${category}Cents`] += amountCents;    // signed; deduction buckets net negative
  }
  return { buckets, lines };
}

// Legacy / edit path: build buckets from flat dollar fields (no itemization).
// Inputs are positive magnitudes; deductions are stored signed-negative to match
// the itemized convention (deduction buckets net negative).
function flatBuckets(raw) {
  const b = zeroBuckets();
  b.commissionCents = -dollarsToCents(pick(raw, 'commission', 'marketplaceFee', 'marketplace_fee'));
  b.marketingCents = -dollarsToCents(pick(raw, 'marketing', 'ads', 'advertising', 'promotions'));
  b.feesCents = -dollarsToCents(pick(raw, 'fees', 'processingFees', 'processing_fees'));
  b.otherCents = -dollarsToCents(pick(raw, 'other', 'adjustments', 'errorCharges', 'error_charges', 'amendments'));
  b.taxCents = dollarsToCents(pick(raw, 'tax', 'taxCollected', 'salesTax', 'sales_tax'));
  b.tipsCents = dollarsToCents(pick(raw, 'tips', 'tip', 'gratuity'));
  return b;
}

/**
 * Normalize a raw OCR delivery-statement extract into a canonical statement
 * record (cents). Prefers itemized `lineItems`; falls back to flat dollar
 * fields (used by the manual edit path).
 *
 * @param {Object} raw - the OCR processor's structured delivery output
 * @param {Object} [hint] - { platform, periodMonth } user-provided fallbacks
 * @returns {Object} canonical statement (sans id/userId/createdAt)
 */
export function normalizeStatement(raw = {}, hint = {}) {
  const platform = normalizePlatform(raw.platform) || normalizePlatform(hint.platform) || null;

  const periodStart = raw.periodStart || raw.period_start || null;
  const periodEnd = raw.periodEnd || raw.period_end || null;
  const periodMonth = monthOf(periodEnd) || monthOf(periodStart) || hint.periodMonth || null;

  let grossSalesCents = dollarsToCents(pick(raw, 'grossSales', 'gross_sales', 'subtotal', 'sales'));
  const netPayoutCents = dollarsToCents(pick(raw, 'netPayout', 'net_payout', 'payout', 'totalPayout', 'deposit'));

  let items = Array.isArray(raw.lineItems) ? raw.lineItems
    : Array.isArray(raw.line_items) ? raw.line_items : null;

  // Robustness: the model sometimes lists sales-CHANNEL lines (Grubhub
  // "Marketplace orders" / "Partner orders") as line items — those are GROSS
  // components, not deductions (and would be miscategorized as commission). If
  // present, derive gross from their sum and drop them from the deduction lines.
  // This keeps Grubhub reconciling whether the model itemizes the sales section
  // (gross = channel sum) or not (gross = the "Restaurant sales" subtotal).
  if (items && items.length) {
    const channels = items.filter((li) => SALES_CHANNEL_RE.test(String(li.label || '')));
    if (channels.length) {
      grossSalesCents = channels.reduce((a, li) => a + dollarsToCents(li.amount), 0);
      items = items.filter((li) => !SALES_CHANNEL_RE.test(String(li.label || '')));
    }
  }

  let buckets, lines;
  if (items && items.length) {
    ({ buckets, lines } = foldLines(items));
    // If the model also surfaced explicit headline tips/tax that no leaf line
    // captured, add them (avoids missing a value; never double-counts a leaf).
    if (buckets.taxCents === 0) buckets.taxCents = dollarsToCents(pick(raw, 'tax', 'salesTax', 'sales_tax'));
    if (buckets.tipsCents === 0) buckets.tipsCents = dollarsToCents(pick(raw, 'tips', 'tip', 'gratuity'));
  } else {
    buckets = flatBuckets(raw);
    lines = [];
  }
  buckets.grossSalesCents = grossSalesCents;
  buckets.netPayoutCents = netPayoutCents;
  for (const k of BUCKET_KEYS) buckets[k] = Number.isFinite(buckets[k]) ? buckets[k] : 0;

  const residualCents = reconcileResidualCents(buckets);
  const needsReview = !platform
    || !periodMonth
    || buckets.grossSalesCents <= 0
    || buckets.netPayoutCents === 0
    || Math.abs(residualCents) > RECONCILE_TOLERANCE_CENTS;

  return {
    platform,
    storeName: raw.storeName || raw.store || null,
    statementNumber: raw.statementNumber || raw.statement_number || null,
    periodMonth,
    periodStart,
    periodEnd,
    ...buckets,
    lines,
    residualCents,
    needsReview,
  };
}
